import React, { useContext, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ModalContext, IModalContext } from 'contexts/modal';
import { IUserContext, UserContext } from 'contexts/user';
import { buildClassName } from 'utils/build-class-name';
import Image from 'components/listing-image';
import CarouselArrows from 'components/swiper-carousel/arrows';
import ShareSaveListing from 'components/share-save-listing';
import NoImage from 'components/image-loader/image/no-image';
import { listingPageModalIds, blurIds } from 'constants/test-constants';
import getListingImageAlt from 'utils/image-alt-description';
import useWindowWidth from 'hooks/use-window-width';
import styles from './style.module.scss';
import { isListingVisibleToUser } from 'utils/listing-agreement-helper';
import { trackEvent } from 'utils/google-tag-manager';
import { GTM_CLICK_ADDRESS_PAGE_IMAGE, GTM_CLICK_ADDRESS_PAGE_IMAGE_RIGHT_ARROW } from 'constants/events';
import { useIsMobile } from 'hooks/use-size-class';
import { useThemeContext } from 'contexts/theme';
import { ThemeNames } from 'types/themes';
import { useTranslation } from 'react-i18next';
import { SiteNameSpace } from 'constants/locale';
import LoadingSkeleton from 'components/loading-skeleton';

import type Listing from 'data/listing';
import type SwiperClass from 'swiper/types/swiper-class';
import type Point from 'data/geojson/point/type';

// Define dynamic map component outside the component
const DynamicMap = dynamic(() => import('components/map/listing'), {
  loading: () => <LoadingSkeleton height="100%" width="100%" />,
  ssr: false,
});

interface Props {
  listingData: {
    listing: Listing;
    initialSlide?: number;
    setActiveIndex?: (index: number) => void;
    isLightbox?: boolean;
  };
  isCrawler?: boolean;
  isModal?: boolean;
}

export default function SwiperCarousel({ listingData, isCrawler, isModal = false }: Props) {
  const MOBILE_INDEX_LOAD_START = 1;
  const DESKTOP_INDEX_LOAD_START = 3;

  const { listing, initialSlide, setActiveIndex, isLightbox } = listingData;
  const { isAuthenticated, acceptedBrowserBasedTerms, acceptedTerms } = useContext(UserContext) as IUserContext;
  const [showsListing, setShowsListing] = useState<boolean>(false);
  const { openModal } = useContext(ModalContext) as IModalContext;
  const windowWidth = useWindowWidth();
  const [swiper, setSwiper] = useState<SwiperClass>();
  const [activeSlide, setActiveSlide] = useState(initialSlide || 0);
  const [isMapActive, setIsMapActive] = useState(false);
  const [carouselItems, setCarouselItems] = useState<JSX.Element[]>([]);
  const [hasLoadedInitialItems, setHasLoadedInitialItems] = useState(false);
  const [hasLoadedAllItems, setHasLoadedAllItems] = useState(false);
  const isPrivate = !isAuthenticated && listing.isPrivate;
  const imageAlt = getListingImageAlt(listing);
  const { imageUrls, imageDescs, isCrea, isItsoVow, providerId, isSignInAndTermsOfUseRequired } = listing;
  const isAddressPageHeader = !isLightbox && !isModal;
  const shouldShowSignInWall = isPrivate || (isSignInAndTermsOfUseRequired && !showsListing && !isAuthenticated);
  const shouldShowTermsWall = !showsListing && (isCrea || isSignInAndTermsOfUseRequired);
  const shouldBlur = shouldShowSignInWall || shouldShowTermsWall;
  const isMobile = useIsMobile();
  const { themeName } = useThemeContext();
  const isExpTheme = themeName !== ThemeNames.ZOOCASA;
  const isMobileLightbox = isMobile && isLightbox;
  const { t } = useTranslation(SiteNameSpace.Modal);
  const hasCoordinates = listing?.position?.coordinates?.length === 2;

  const carouselImages = imageUrls.map(
    (image, index) => ({
      image,
      index,
      id: `${index}-carousel-${listing.id}`,
      alt: imageDescs && imageDescs.has(index.toString()) ?
        `${imageDescs.get(index.toString())} | Image ${index + 1}` : `${imageAlt} | Image ${index + 1}`,
      imageDesc: (imageDescs && imageDescs.has(index.toString())) ?
        imageDescs.get(index.toString()) : '',
    }));

  // Initialize carousel items on mount
  useEffect(() => {
    if (!hasLoadedInitialItems && carouselImages.length > 0) {
      loadInitialCarouselItems();
    }
  }, [hasLoadedInitialItems, carouselImages.length]);

  // Load remaining items when needed
  useEffect(() => {
    if (!hasLoadedAllItems && hasLoadedInitialItems && carouselImages.length > 0) {
      loadRestOfItems();
    }
  }, [hasLoadedAllItems, hasLoadedInitialItems, carouselImages.length]);

  useEffect(() => {
    if (listing.providerId) {
      setShowsListing(isListingVisibleToUser(isPrivate, listing.providerId, acceptedBrowserBasedTerms, acceptedTerms));
    }
  }, [isPrivate, listing.providerId, acceptedBrowserBasedTerms, acceptedTerms]);

  const openPhotoGallery = (photoIndex?: number | string) => {
    if (!isLightbox && listing) {
      openModal('photo-gallery', { listing, photoIndex, isModal });
      if (!isModal) {
        trackEvent(GTM_CLICK_ADDRESS_PAGE_IMAGE);
      }
    }
  };

  const loadInitialCarouselItems = () => { // Only for desktop
    if (!isMobile && !hasLoadedInitialItems) {
      const items = (carouselImages.slice(1, !isLightbox ? DESKTOP_INDEX_LOAD_START : undefined) || []).map((item, index) => (
        <SwiperSlide key={index + 1 + (isLightbox? 'fullscreen': '')} className={buildClassName(styles['carousel-item'], isLightbox && styles.full)}>
          <Image
            item={item}
            onClick={() => openPhotoGallery(item.index)}
            isLightbox={isLightbox}
            isCrawler={index < 4 && isCrawler}
            width={windowWidth}
            isVisible={isMobile && index <= 1 ? true : !isMobile && index <= 4}
            loadLowQualityFirst={!isModal}
          />
        </SwiperSlide>
      ));

      if (carouselImages.length < DESKTOP_INDEX_LOAD_START && listing.position && !isLightbox && !!carouselImages?.length && hasCoordinates) { // if not enough images, show map in initial items
        items.push(
          <SwiperSlide key="map-slide" className={buildClassName(styles['carousel-item'], isLightbox && styles.full, styles['no-image'])}>
            <span onClick={() => openPhotoGallery('map')}>
              <DynamicMap
                listingId={listing!.id}
                position={(listing.position as unknown as Point)}
                className={styles.map}
                isInteractive={false}
              />
            </span>
          </SwiperSlide>
        );
      }
      setCarouselItems(items);
      setHasLoadedInitialItems(true);
    }
  };

  const buildFirstImage = () => {
    if (!!carouselImages?.length) {
      const initialImageIndex = initialSlide || 0;
      return (
        <SwiperSlide key={initialImageIndex} className={buildClassName(styles['carousel-item'], isLightbox && styles.full)}>
          <Image
            item={carouselImages[initialImageIndex]}
            onClick={() => openPhotoGallery(initialImageIndex)}
            isVisible={true}
            isLightbox={isLightbox}
            isCrawler={isCrawler}
            width={windowWidth}
            loadLowQualityFirst={!isModal}
          />
        </SwiperSlide>
      );
    } else {
      return (
        <SwiperSlide key={0} className={buildClassName(styles['carousel-item'], isLightbox && styles.full, styles['no-image'])}>
          <NoImage isExpTheme={isExpTheme} />
        </SwiperSlide>
      );
    }
  };

  const firstImage = buildFirstImage();

  const loadRestOfItems = () => {
    if (!hasLoadedAllItems && !!carouselImages?.length) {
      const INDEX_LOAD_START = isMobile ? isLightbox ? 0 : MOBILE_INDEX_LOAD_START : DESKTOP_INDEX_LOAD_START;
      const newItems = (carouselImages.slice(INDEX_LOAD_START) || []).map((item, index) => (
        <SwiperSlide key={isMobile ? index + MOBILE_INDEX_LOAD_START : index + DESKTOP_INDEX_LOAD_START} className={buildClassName(styles['carousel-item'], isLightbox && styles.full)}>
          <Image
            item={item}
            onClick={() => openPhotoGallery(item.index)}
            isLightbox={isLightbox}
            isCrawler={index < 4 && isCrawler}
            width={windowWidth}
            isVisible={isMobile && index <= 1 ? true : !isMobile && index <= 4}
          />
        </SwiperSlide>
      ));

      // Add map for mobile now or if it wasn't added in the initial load for desktop
      if ((isMobile || carouselImages.length >= 3) && hasCoordinates && !isLightbox) {
        newItems.push(
          <SwiperSlide key="map-slide" className={buildClassName(styles['carousel-item'], isLightbox && styles.full, styles['no-image'])}>
            <span onClick={() => openPhotoGallery('map')}>
              <DynamicMap
                listingId={listing!.id}
                position={(listing.position as unknown as Point)}
                className={styles.map}
                isInteractive={false}
              />
            </span>
          </SwiperSlide>
        );
      }

      setCarouselItems([...carouselItems, ...newItems]);
      setHasLoadedAllItems(true);
    }
  };

  const slideTo = (slide: 'next' | 'prev' | number) => {
    let targetSlide = null;
    if (slide === 'next') {
      targetSlide = activeSlide < carouselItems.length - 1 ? activeSlide + 1 : 0;
      if (!isModal) {
        trackEvent(GTM_CLICK_ADDRESS_PAGE_IMAGE_RIGHT_ARROW);
      }
    } else if (slide === 'prev') {
      targetSlide = activeSlide > 0 ? activeSlide - 1 : carouselItems.length - 1;
    } else {
      targetSlide = slide;
    }

    swiper?.slideTo(targetSlide);
    setActiveSlide(targetSlide);
  };

  const openSignInModal = () => {
    if (isPrivate || (isItsoVow && !showsListing && !isAuthenticated)) {
      openModal('login-registration', {
        warningMessage: t('login-registration.form.signinRequiredWarning'),
      });
    } else if (!showsListing) {
      openModal('terms-of-use', { providerId });
    }
  };

  useEffect(() => {
    if (activeSlide !== (initialSlide || 0)) {
      setIsMapActive(true);
    }

    setActiveIndex && setActiveIndex(activeSlide);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlide, setActiveIndex]);

  const renderOverlay = () => {
    if (shouldShowSignInWall) {
      return (
        <div className={styles['sign-in-overlay']} onClick={openSignInModal} data-testid={blurIds.blurredListingImageCarousel}>
          <div className={styles.cta} data-testid={blurIds.overlaySignInMsg}>
            Real Estate boards require that you sign in to see the full details of this property.
            <span data-testid={blurIds.overlaySignInBtn}>Sign in to view images</span>
          </div>
        </div>
      );
    } else if (shouldShowTermsWall) {
      return (
        <div className={styles['sign-in-overlay']} onClick={openSignInModal} data-testid={blurIds.blurredListingImageCarousel}>
          <div className={styles.cta} data-testid={blurIds.overlayAcceptTermsBtn}>
            Real Estate boards require that you accept Terms of Use.
            <span data-testid={blurIds.overlayAcceptTermsMsg}>Accept Terms of Use to view images</span>
          </div>
        </div>
      );
    }
  };

  if (isMobileLightbox && !hasLoadedAllItems) {
    loadRestOfItems();
  }

  return (
    <div className={buildClassName(styles.component, styles.margin)} data-testid={listingPageModalIds.listingImageCarousel} onMouseOver={!isLightbox ? loadRestOfItems : undefined}>
      <Swiper
        onSwiper={setSwiper}
        spaceBetween={0}
        slidesPerView={1}
        initialSlide={initialSlide}
        onSlideChangeTransitionStart={() => !isLightbox && loadRestOfItems}
        onTouchStart={() => !isLightbox && loadRestOfItems}
        onSlideChange={(swiper: SwiperClass) => {
          setActiveSlide(swiper.realIndex);
          if (!hasLoadedAllItems && !isLightbox) {
            loadRestOfItems();
          }
        }}
        breakpoints={{ 768: { slidesPerView: 'auto' }}}
        className={buildClassName(styles.slider, shouldBlur && styles['blur-image'], isAddressPageHeader && styles.header)}
        data-testid={listingPageModalIds.swiperCarouselComponent}
      >
        {(!isMobileLightbox || !isLightbox) && firstImage}
        {carouselItems}
      </Swiper>
      {renderOverlay()}
      {!isMobileLightbox && !!carouselImages?.length && <CarouselArrows isLightbox={isLightbox} isListingCardCarousel={false} slideTo={slideTo} className={styles.arrow} />}
      {listing && !isLightbox && <ShareSaveListing listing={listing} isModal={isModal} />}
    </div>
  );
}
